
import React, { ReactNode, useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { FaChevronDown, FaChevronRight, FaDatabase, FaPen, FaPlus, FaRegFolderOpen, FaSearch, FaTable, FaTrash } from 'react-icons/fa';
import {
  createInternalRegisteredSourceEntity,
  updateInternalRegisteredSourceEntity,
  getSourceEntityForEdit,
  getBronzeCatalogColumns
} from '../../services/catalogue.service';
import { deleteEndPointDetails } from '../../services/dataSource.service';
import { getDataVaultRelatedEntities } from '../../services/dataVaultModelling.service';
import { IDataVaultRelatedEntity } from '../../interfaces/dataVaultModelling.interfaces';
import './selectDataSet.scss';
import * as api from '../../services/middleware/api.middleware';
import './index.scss';
import { compareNames } from "../../helpers/hubOptions";

interface ExampleComponentProps {
  setData: any;
  selectedItems: any;
  formattedString: any;
  multipleSelection: string[];
  setMultipleSelection: any;
  selectedEndpoints: string[];
  setSelectedEndpoints: any;
  setBronzeSelectionIdentity: (identity: {
    dataSourceId: number | null;
    sourceEntityId: number | null;
    sourceEntityLayer: "bronze" | "silver" | "gold" | null;
    dataSourceType: string | null;
  }) => void;
  setShowErrorModal: any;
  currentPage: number;
  dataMartMappings: any[];
  dataMartOptions: any[];
  selectedDataMarts: string[];
  setSelectedDataMarts: any;
  selectedDataMartEntities: string[];
  setSelectedDataMartEntities: any;
  isLoadingDataMartMappings: boolean;
  activeRelatedEntity: IDataVaultRelatedEntity | null;
  setActiveRelatedEntity: (entity: IDataVaultRelatedEntity | null) => void;
}

type SourcePanelKey = "primary" | "related" | "dataMart";
type CategoryKey = "category-primary" | "category-related" | "category-dataMart";

type DataSourceListItem = {
  data_source_id: number | null;
  data_source_name: string;
  data_source_type?: string | null;
  data_source_description?: string | null;
  is_active?: boolean;
  endpoints: Array<{
    source_entity_id: number | null;
    source_entity_name: string;
    source_entity_layer?: string | null;
    source_entity_kind?: string | null;
    source_entity_origin?: string | null;
    source_entity_description?: string | null;
    is_active?: boolean;
  }>;
};

type DataMartEntity = {
  name: string;
  table_construct?: string | null;
  target_schema?: string | null;
  data_vault_schema?: string | null;
  target_entity_rdv_name?: string | null;
};

type DataMartGroup = {
  datamart: string;
  entities: DataMartEntity[];
};

const normalizeBronzeCatalogRows = (rows: any[] = [], entity?: any) =>
  rows.map((row) => ({
    data_source_id: entity?.data_source_id ?? null,
    data_source_name: entity?.data_source_name ?? null,
    data_source_type: entity?.data_source_type ?? null,
    source_entity_id: entity?.source_entity_id ?? null,
    source_entity_name: entity?.source_entity_name ?? null,
    source_schema: entity?.source_schema ?? null,
    source_entity_layer: entity?.source_entity_layer ?? null,
    source_entity_kind: entity?.source_entity_kind ?? null,
    source_entity_origin: entity?.source_entity_origin ?? null,
    source_entity_description: entity?.source_entity_description ?? null,
    source_entity_is_active: entity?.is_active ?? true,
    source_entity_column_id: row?.source_entity_column_id ?? null,
    source_entity_column_detail_id: row?.details?.source_entity_column_detail_id ?? null,
    source_entity_column_name: row?.source_entity_column_name ?? null,
    data_type: row?.data_type ?? null,
    is_obsolete: row?.is_obsolete ?? false,
    logical_name: row?.details?.logical_name ?? null,
    description: row?.details?.description ?? null,
    constraint: row?.details?.constraint ?? null,
    data_format: row?.details?.data_format ?? null,
    example_values: row?.details?.example_values ?? null,
    enable_genai: row?.details?.enable_genai ?? false,
    pii: row?.details?.pii ?? false,
    classification: row?.details?.classification ?? null,
    owner: row?.details?.owner ?? null,
    notes: row?.details?.notes ?? null,
  }));

const createEmptyInternalColumn = () => ({
  source_entity_column_name: '',
  data_type: 'string',
  logical_name: '',
  description: '',
  enable_genai: false,
  pii: false,
});

const INTERNAL_ASSET_KIND_OPTIONS = [
  { value: "table", label: "Table" },
  { value: "view", label: "View" },
  { value: "union_view", label: "Union View" },
];

const INTERNAL_ASSET_LAYER_OPTIONS = [
  { value: "bronze", label: "Bronze" },
  { value: "silver", label: "Silver" },
  { value: "gold", label: "Gold" },
];

// These MUST match the backend INTERNAL_REGISTERED_ROOTS data_source_name values exactly — they are
// used as data lookups (data_source_name === ...). Renamed in lockstep with the backend constant +
// the data migration 2026-06-29_rename_internal_asset_roots_to_custom.
const INTERNAL_BRONZE_ROOT_NAME = "Custom Source Entities";
const INTERNAL_SILVER_ROOT_NAME = "Custom Data Vault Entities";
const INTERNAL_GOLD_ROOT_NAME = "Custom Data Mart Entities";

const SelectedDataSet: React.FC<ExampleComponentProps> = ({
  setData,
  selectedItems,
  formattedString,
  multipleSelection,
  setMultipleSelection,
  selectedEndpoints,
  setSelectedEndpoints,
  setBronzeSelectionIdentity,
  setShowErrorModal,
  currentPage,
  dataMartMappings,
  dataMartOptions,
  selectedDataMarts,
  setSelectedDataMarts,
  selectedDataMartEntities,
  setSelectedDataMartEntities,
  isLoadingDataMartMappings,
  activeRelatedEntity,
  setActiveRelatedEntity,
}) => {
  const [dataSourceList, setDataSourceList] = useState<DataSourceListItem[]>([]);
  const [relatedEntities, setRelatedEntities] = useState<IDataVaultRelatedEntity[]>([]);
  const [expandedDataSources, setExpandedDataSources] = useState<string[]>([]);
  const [expandedDataMarts, setExpandedDataMarts] = useState<string[]>([]);
  const [expandedCategories, setExpandedCategories] = useState<CategoryKey[]>([]);
  const [expandedSilverGroups, setExpandedSilverGroups] = useState<string[]>([
    "silver-raw-data-vault",
    "silver-business-data-vault",
  ]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [registeringInternalAsset, setRegisteringInternalAsset] = useState(false);
  const [registerInternalAssetError, setRegisterInternalAssetError] = useState("");
  const [deletingInternalAssetKey, setDeletingInternalAssetKey] = useState<string | null>(null);
  // When set, the Register modal is reused as an EDIT modal for an existing custom source entity.
  // null = create mode.
  const [editingInternalAsset, setEditingInternalAsset] = useState<{
    dataSourceId: number;
    sourceEntityId: number;
  } | null>(null);
  const [internalAssetForm, setInternalAssetForm] = useState({
    sourceEntityName: "",
    sourceEntityDescription: "",
    sourceSchema: "bronze",
    sourceEntityLayer: "bronze",
    sourceEntityKind: "table",
    definitionSql: "",
    sourceEntityActive: true,
    columns: [createEmptyInternalColumn()],
  });
  const deferredSearchTerm = useDeferredValue(searchTerm);
  const normalizedSearchTerm = deferredSearchTerm.trim().toLowerCase();
  const getEnvironmentId = () => localStorage.getItem('environment_id');
  const environmentId = getEnvironmentId();
  const dataMartNameById = useMemo(() => {
    if (!Array.isArray(dataMartOptions)) {
      return new Map<number, string>();
    }
    return dataMartOptions.reduce((acc, item) => {
      const id = Number(item?.data_mart_id);
      const name = typeof item?.data_mart === "string" ? item.data_mart.trim() : "";
      if (Number.isFinite(id) && name) {
        acc.set(id, name);
      }
      return acc;
    }, new Map<number, string>());
  }, [dataMartOptions]);

  const dataMartGroups = useMemo<DataMartGroup[]>(() => {
    if (!Array.isArray(dataMartMappings)) {
      return [];
    }

    const grouped = dataMartMappings.reduce((acc: Record<string, DataMartEntity[]>, item: any) => {
      const dataMartId = Number(item?.data_mart_id);
      const mappedName = Number.isFinite(dataMartId)
        ? dataMartNameById.get(dataMartId)
        : undefined;
      const groupName = mappedName || item?.datamart || item?.data_mart || "Unassigned Data Marts";
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      if (item?.target_entity_dm_name) {
        const exists = acc[groupName].some(entity => entity.name === item.target_entity_dm_name);
        if (!exists) {
          acc[groupName].push({
            name: item.target_entity_dm_name,
            table_construct: item?.table_construct ?? item?.target_entity_table_construct ?? null,
            target_schema: item?.target_schema ?? null,
            data_vault_schema: item?.data_vault_schema ?? null,
            target_entity_rdv_name: item?.target_entity_rdv_name ?? null,
          });
        }
      }
      return acc;
    }, {});

    return Object.entries(grouped).map(([datamart, entities]) => ({
      datamart,
      entities,
    }));
  }, [dataMartMappings, dataMartNameById]);
  // const lastChange = useRef<{ type: 'ds' | 'ep'; value: string } | null>(null);

  // after:
  const lastChange = useRef<
    | { type: 'ds'; value: string; panel: SourcePanelKey }
    | { type: 'ep'; value: string; parent: string; panel: SourcePanelKey }
    | { type: 'dm'; value: string; entities: string[] }
    | { type: 'dmEntity'; value: string; parent: string }
    | null
  >(null);
  const hasMountedFilters = useRef(false);

  const getAccessToken = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

  const fetchDataSources = useCallback(async () => {
    try {
      const resp = await api.get<any>(
        `environments/${environmentId}/data_sources`,
        getAccessToken()
      );
      const endpoints = resp.map((ds: any) => ({
        data_source_id: Number(ds?.data_source_id ?? ds?.id ?? null),
        data_source_name: ds.data_source_name,
        data_source_type: ds?.data_source_type ?? null,
        data_source_description: ds?.data_source_description ?? null,
        is_active: Boolean(ds?.is_active ?? true),
        endpoints: (ds.source_entity ?? []).map((e: any) => ({
          source_entity_id: Number(e?.source_entity_id ?? e?.id ?? null),
          source_entity_name: e.source_entity_name,
          source_entity_layer: e?.source_entity_layer ?? null,
          source_entity_kind: e?.source_entity_kind ?? null,
          source_entity_origin: e?.source_entity_origin ?? null,
          source_entity_description: e?.source_entity_description ?? null,
          is_active: Boolean(e?.is_active ?? true),
        })),
      }));
      setDataSourceList(endpoints);
      return endpoints as DataSourceListItem[];
    } catch (err) {
      console.error('Error fetching data sources:', err);
      return [];
    }
  }, [environmentId]);

  const findDataSourceByName = useCallback((name: string | null | undefined) => {
    if (!name) {
      return undefined;
    }
    return dataSourceList.find((ds) => ds.data_source_name === name);
  }, [dataSourceList]);

  const findDataMartGroup = useCallback((name: string) =>
    dataMartGroups.find(group => group.datamart === name), [dataMartGroups]);

  const resetInternalAssetForm = useCallback(() => {
    setRegisterInternalAssetError("");
    setInternalAssetForm({
      sourceEntityName: "",
      sourceEntityDescription: "",
      sourceSchema: "bronze",
      sourceEntityLayer: "bronze",
      sourceEntityKind: "table",
      definitionSql: "",
      sourceEntityActive: true,
      columns: [createEmptyInternalColumn()],
    });
  }, []);

  const handleOpenRegisterModal = useCallback((layer: "bronze" | "silver" | "gold" = "bronze") => {
    setEditingInternalAsset(null);
    resetInternalAssetForm();
    setInternalAssetForm(prev => ({
      ...prev,
      sourceSchema: layer,
      sourceEntityLayer: layer,
    }));
    setShowRegisterModal(true);
  }, [resetInternalAssetForm]);

  // Reuse the Register modal as an EDIT modal. Open immediately with the list data we already have,
  // then fetch the full entity (source_schema + definition_sql are NOT in the list-fetch) and fill
  // every field with real values so all fields are genuinely editable and a save can't blank them.
  // Columns are NOT edited here (handled by the column manager) — the Initial-columns section is
  // hidden in edit mode. Layer is read-only because create routes the asset into a layer-specific
  // internal root and the PUT does not move it.
  const handleOpenEditModal = useCallback(async (
    dataSource: DataSourceListItem,
    endpoint: DataSourceListItem["endpoints"][number],
  ) => {
    const dataSourceId = Number(dataSource?.data_source_id ?? null);
    const sourceEntityId = Number(endpoint?.source_entity_id ?? null);
    if (!Number.isFinite(dataSourceId) || !Number.isFinite(sourceEntityId)) {
      console.error("Missing source entity identity for internal asset edit.");
      return;
    }

    setRegisterInternalAssetError("");
    setEditingInternalAsset({ dataSourceId, sourceEntityId });
    // Seed from list data so the modal opens instantly with name/layer/kind.
    setInternalAssetForm(prev => ({
      ...prev,
      sourceEntityName: endpoint.source_entity_name ?? "",
      sourceEntityDescription: endpoint.source_entity_description ?? "",
      sourceSchema: "",
      sourceEntityLayer: (endpoint.source_entity_layer as string) ?? "bronze",
      sourceEntityKind: (endpoint.source_entity_kind as string) ?? "table",
      definitionSql: "",
      sourceEntityActive: endpoint.is_active ?? true,
      columns: [createEmptyInternalColumn()],
    }));
    setShowRegisterModal(true);

    // Fetch the full entity to fill in schema + SQL (and correct any list/detail drift).
    try {
      const full: any = await getSourceEntityForEdit({ dataSourceId, sourceEntityId });
      const entity = full?.data ?? full;
      if (entity) {
        setInternalAssetForm(prev => ({
          ...prev,
          sourceEntityName: entity.source_entity_name ?? prev.sourceEntityName,
          sourceEntityDescription: entity.source_entity_description ?? "",
          sourceSchema: entity.source_schema ?? "",
          sourceEntityLayer: entity.source_entity_layer ?? prev.sourceEntityLayer,
          sourceEntityKind: entity.source_entity_kind ?? prev.sourceEntityKind,
          definitionSql: entity.definition_sql ?? "",
          sourceEntityActive: entity.is_active ?? prev.sourceEntityActive,
        }));
      }
    } catch (error) {
      console.error("Could not load full custom source entity for edit:", error);
      setRegisterInternalAssetError("Could not load all fields for this entity. Schema/SQL may be incomplete.");
    }
  }, []);

  const updateInternalAssetForm = useCallback((field: string, value: any) => {
    setInternalAssetForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const toggleCategory = (key: CategoryKey) => {
    setExpandedCategories(prev =>
      prev?.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
    );
  };

  const toggleSilverGroup = (key: string) => {
    setExpandedSilverGroups(prev =>
      prev?.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
    );
  };

  const toggleDataMartGroup = (name: string) => {
    setExpandedDataMarts(prev =>
      prev?.includes(name) ? prev.filter(item => item !== name) : [...prev, name]
    );
  };

  const handleDataMartGroupSelect = (groupName: string) => {
    const group = findDataMartGroup(groupName);
    if (!group) {
      return;
    }

    const currentlySelected = selectedDataMarts?.includes(groupName);
    const entityNames = group.entities
      .map(entity => entity.name)
      .filter((entity): entity is string => Boolean(entity));
    lastChange.current = { type: 'dm', value: groupName, entities: entityNames };

    if (currentlySelected) {
      setSelectedDataMarts([]);
      setSelectedDataMartEntities([]);
    } else {
      setSelectedDataMarts([groupName]);
      if (entityNames.length > 0) {
        setSelectedDataMartEntities([entityNames[0]]);
      } else {
        setSelectedDataMartEntities([]);
      }
      setMultipleSelection([]);
      setSelectedEndpoints([]);
      setActiveRelatedEntity(null);
    }

    toggleDataMartGroup(groupName);
  };

  const handleDataMartEntitySelect = (entity: DataMartEntity, groupName: string) => {
    const entityName = entity?.name;
    if (!entityName) {
      return;
    }

    const group = findDataMartGroup(groupName);
    if (!group) {
      return;
    }

    lastChange.current = { type: 'dmEntity', value: entityName, parent: groupName };
    const entitySelected = selectedDataMartEntities?.includes(entityName);

    if (entitySelected) {
      setSelectedDataMartEntities([]);
      setSelectedDataMarts([]);
    } else {
      setSelectedDataMartEntities([entityName]);
      setSelectedDataMarts([groupName]);
      setMultipleSelection([]);
      setSelectedEndpoints([]);
      setActiveRelatedEntity(null);
    }
  };

  useEffect(() => {
    fetchDataSources();
  }, [fetchDataSources]);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      try {
        const relatedEntitiesResponse = await getDataVaultRelatedEntities();
        if (!isMounted) {
          return;
        }

        // A row with no target entity is normally noise (a source nobody modelled yet),
        // but a customer-maintained DV object is registered for its own sake and has no
        // RDV mapping — dropping those emptied the Custom Data Vault Entities folder.
        const filteredRelatedEntities = Array.isArray(relatedEntitiesResponse)
          ? relatedEntitiesResponse.filter(
              (entity: IDataVaultRelatedEntity) =>
                Boolean(entity?.target_entity_rdv_name) ||
                (entity?.data_source_name === INTERNAL_SILVER_ROOT_NAME &&
                  Boolean(entity?.source_entity_name))
            )
          : [];

        if (filteredRelatedEntities.length === 0) {
          setRelatedEntities([]);
          return;
        }

        setRelatedEntities(filteredRelatedEntities);

        const grouped = filteredRelatedEntities.reduce((acc, entity) => {
          if (!entity?.data_source_name) {
            return acc;
          }

          if (!acc.has(entity.data_source_name)) {
            acc.set(entity.data_source_name, new Set<string>());
          }

          if (entity.source_entity_name) {
            acc.get(entity.data_source_name)!.add(entity.source_entity_name);
          }

          return acc;
        }, new Map<string, Set<string>>());

        const fallbackList = Array.from(grouped.entries()).map(([name, endpoints]) => ({
          data_source_id: null,
          data_source_name: name,
          endpoints: Array.from(endpoints).map(endpoint => ({
            source_entity_id: null,
            source_entity_name: endpoint,
          })),
        }));

        setDataSourceList(prev =>
          Array.isArray(prev) && prev.length > 0 ? prev : fallbackList
        );
      } catch (error) {
        console.error('Error fetching related entities for data sources:', error);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, []);

  const createDataSourceKey = (panelKey: SourcePanelKey, name: string) =>
    `${panelKey}::${name}`;

  const toggleDataSource = (panelKey: SourcePanelKey, name: string) => {
    const dsKey = createDataSourceKey(panelKey, name);
    setExpandedDataSources((prev = []) =>
      prev.includes(dsKey)
        ? prev.filter((key) => key !== dsKey)
        : [...prev, dsKey]
    );
  };

  // const handleDataSourceSelect = (name: string) => {
  //   setMultipleSelection((prev: string[] = []) =>
  //     prev.includes(name)
  //       ? prev.filter(x => x !== name)
  //       : [...prev, name]
  //   );
  //   toggleDataSource(name);
  // };

  // const handleEndpointSelect = (endpoint: string, dsName: string) => {
  //   setSelectedEndpoints((prev: string[] = []) =>
  //     prev.includes(endpoint) ? prev.filter(x => x !== endpoint) : [...prev, endpoint]
  //   );
  //   if (!multipleSelection.includes(dsName)) {
  //     setMultipleSelection((prev:any) => [...prev, dsName]);
  //     // @ts-ignore
  //     setExpandedDataSources((prev => [...new Set([...prev, dsName])]));
  //   }
  // };
  const handleDataSourceSelect = (name: string, panelKey: SourcePanelKey) => {
    const isSelected = multipleSelection?.includes(name);
    lastChange.current = { type: "ds", value: name, panel: panelKey };

    if (isSelected) {
      setMultipleSelection([]);
      setSelectedEndpoints([]);
      setBronzeSelectionIdentity({ dataSourceId: null, sourceEntityId: null, sourceEntityLayer: null, dataSourceType: null });
      return;
    }

    setMultipleSelection([name]);
    setSelectedEndpoints([]);
    setBronzeSelectionIdentity({ dataSourceId: null, sourceEntityId: null, sourceEntityLayer: null, dataSourceType: null });
    setSelectedDataMarts([]);
    setSelectedDataMartEntities([]);
    setActiveRelatedEntity(null);

    setExpandedDataSources(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const dsKey = createDataSourceKey(panelKey, name);
      return arr.includes(dsKey) ? arr : [...arr, dsKey];
    });
  };

  // const handleEndpointSelect = (endpoint: string, dsName: string) => {
  //   // lastChange.current = { type: 'ep', value: endpoint };
  //   lastChange.current = { type: 'ep', value: endpoint, parent: dsName };
  //   setSelectedEndpoints((prev:any) =>
  //     prev?.includes(endpoint) ? prev?.filter((x:any) => x !== endpoint) : [...prev, endpoint]
  //   );
  //   if (!multipleSelection?.includes(dsName)) {
  //     setMultipleSelection((prev:any) => [...prev, dsName]);
  //     // @ts-ignore
  //     setExpandedDataSources((prev:any) => [...new Set([...prev, dsName])]);
  //   }
  //   // no more handleSave()
  // };

  const handleEndpointSelect = (
    endpoint: string,
    dsName: string,
    panelKey: SourcePanelKey,
    layer?: "bronze" | "silver" | "gold" | null
  ) => {
    const alreadySelected = selectedEndpoints?.includes(endpoint);
    lastChange.current = { type: "ep", value: endpoint, parent: dsName, panel: panelKey };

    if (alreadySelected) {
      setSelectedEndpoints([]);
      setMultipleSelection([]);
      setBronzeSelectionIdentity({ dataSourceId: null, sourceEntityId: null, sourceEntityLayer: null, dataSourceType: null });
      return;
    }

    const dataSource = findDataSourceByName(dsName);
    const selectedEndpoint = dataSource?.endpoints.find(item => item.source_entity_name === endpoint);

    setSelectedEndpoints([endpoint]);
    setMultipleSelection([dsName]);
    setBronzeSelectionIdentity({
      dataSourceId: Number(dataSource?.data_source_id ?? null),
      sourceEntityId: Number(selectedEndpoint?.source_entity_id ?? null),
      sourceEntityLayer: layer ?? (selectedEndpoint?.source_entity_layer as "bronze" | "silver" | "gold" | null) ?? "bronze",
      dataSourceType: dataSource?.data_source_type ?? null,
    });
    setSelectedDataMarts([]);
    setSelectedDataMartEntities([]);
    setActiveRelatedEntity(null);

    setExpandedDataSources(prev => {
      const arr = Array.isArray(prev) ? prev : [];
      const dsKey = createDataSourceKey(panelKey, dsName);
      return arr.includes(dsKey) ? arr : [...arr, dsKey];
    });
  };

  const handleRegisterInternalAsset = useCallback(async () => {
    if (!environmentId) {
      console.error("Missing environment context for internal asset registration.");
      return;
    }

    const trimmedEntityName = internalAssetForm.sourceEntityName.trim();
    if (!trimmedEntityName) {
      setRegisterInternalAssetError("Source entity name is required.");
      return;
    }

    setRegisteringInternalAsset(true);
    setRegisterInternalAssetError("");

    // EDIT mode: PUT only the entity-level fields (no columns — the update endpoint uses
    // exclude_unset, so omitting columns leaves them untouched). layer/kind/schema are read-only in
    // the edit UI but sent for completeness.
    if (editingInternalAsset) {
      try {
        // All fields are prefilled from the full-entity fetch, so we send them directly. Layer is
        // read-only (the asset can't move between layer-specific internal roots).
        await updateInternalRegisteredSourceEntity({
          dataSourceId: editingInternalAsset.dataSourceId,
          sourceEntityId: editingInternalAsset.sourceEntityId,
          payload: {
            source_entity_name: trimmedEntityName,
            source_entity_description: internalAssetForm.sourceEntityDescription.trim() || null,
            source_schema: internalAssetForm.sourceSchema.trim(),
            source_entity_layer: internalAssetForm.sourceEntityLayer.trim(),
            source_entity_kind: internalAssetForm.sourceEntityKind.trim(),
            definition_sql: internalAssetForm.definitionSql.trim() || null,
          },
        });
        await fetchDataSources();
        setShowRegisterModal(false);
        setEditingInternalAsset(null);
      } catch (error: any) {
        console.error("Error updating internal asset:", error);
        const conflict = error?.response?.status === 409 || error?.status === 409;
        setRegisterInternalAssetError(
          conflict
            ? "An entity with this name already exists."
            : "Could not update the custom source entity."
        );
      } finally {
        setRegisteringInternalAsset(false);
      }
      return;
    }

    try {
      // Columns are no longer created here — the entity is created empty, then columns are added via
      // the dedicated bulk "Add column" grid after selecting the entity in the catalog.
      const createdEntity = await createInternalRegisteredSourceEntity({
        payload: {
          source_entity_name: trimmedEntityName,
          source_entity_description: internalAssetForm.sourceEntityDescription.trim() || null,
          source_schema: internalAssetForm.sourceSchema.trim(),
          source_entity_layer: internalAssetForm.sourceEntityLayer.trim(),
          source_entity_kind: internalAssetForm.sourceEntityKind.trim(),
          definition_sql: internalAssetForm.definitionSql.trim() || null,
          is_active: internalAssetForm.sourceEntityActive,
        },
      });

      const createdSourceEntityId = Number(
        createdEntity?.source_entity_id ??
        createdEntity?.data?.source_entity_id
      );
      const createdSourceEntityName =
        createdEntity?.source_entity_name ??
        createdEntity?.data?.source_entity_name ??
        trimmedEntityName;

      await fetchDataSources();

      setExpandedCategories(prev =>
        prev.includes("category-primary") ? prev : [...prev, "category-primary"]
      );
      setExpandedDataSources(prev => {
        const createdSource = (createdEntity?.data_source_name || createdEntity?.data?.data_source_name || "") as string;
        const dsKey = createDataSourceKey("primary", createdSource);
        return prev.includes(dsKey) ? prev : [...prev, dsKey];
      });
      setMultipleSelection([
        createdEntity?.data_source_name ??
        createdEntity?.data?.data_source_name ??
        ""
      ].filter(Boolean));
      setSelectedEndpoints([createdSourceEntityName]);
      setBronzeSelectionIdentity({
        dataSourceId: Number(
          createdEntity?.data_source_id ??
          createdEntity?.data?.data_source_id ??
          null
        ),
        sourceEntityId: Number.isFinite(createdSourceEntityId) ? createdSourceEntityId : null,
        sourceEntityLayer: (internalAssetForm.sourceEntityLayer as "bronze" | "silver" | "gold" | null) ?? null,
        dataSourceType:
          createdEntity?.data_source_type ??
          createdEntity?.data?.data_source_type ??
          "INTERNAL_REGISTERED",
      });
      setSelectedDataMarts([]);
      setSelectedDataMartEntities([]);
      setActiveRelatedEntity(null);
      setShowRegisterModal(false);
    } catch (error) {
      console.error("Error registering internal asset:", error);
      setRegisterInternalAssetError("Could not register the internal asset.");
    } finally {
      setRegisteringInternalAsset(false);
    }
  }, [
    editingInternalAsset,
    environmentId,
    fetchDataSources,
    internalAssetForm,
    setActiveRelatedEntity,
    setBronzeSelectionIdentity,
    setMultipleSelection,
    setSelectedDataMarts,
    setSelectedDataMartEntities,
    setSelectedEndpoints,
  ]);

  const handleDeleteInternalAsset = useCallback(async (
    dataSource: DataSourceListItem,
    endpoint: DataSourceListItem["endpoints"][number],
    layer: "bronze" | "silver" | "gold"
  ) => {
    const dataSourceId = Number(dataSource?.data_source_id ?? null);
    const sourceEntityId = Number(endpoint?.source_entity_id ?? null);

    if (!Number.isFinite(dataSourceId) || !Number.isFinite(sourceEntityId) || !environmentId) {
      console.error("Missing source entity identity for internal asset delete.");
      return;
    }

    const confirmed = window.confirm(
      `Delete internal ${layer} asset "${endpoint.source_entity_name}"?`
    );
    if (!confirmed) {
      return;
    }

    const deleteKey = `${dataSourceId}:${sourceEntityId}`;
    setDeletingInternalAssetKey(deleteKey);
    try {
      await deleteEndPointDetails(dataSourceId, sourceEntityId);
      await fetchDataSources();

      const isCurrentSelection =
        multipleSelection?.[0] === dataSource.data_source_name &&
        selectedEndpoints?.[0] === endpoint.source_entity_name;

      if (isCurrentSelection) {
        setMultipleSelection([]);
        setSelectedEndpoints([]);
        setBronzeSelectionIdentity({
          dataSourceId: null,
          sourceEntityId: null,
          sourceEntityLayer: null,
          dataSourceType: null,
        });
        setSelectedDataMarts([]);
        setSelectedDataMartEntities([]);
        setActiveRelatedEntity(null);
        setData({
          items: [],
          total_count: 0,
          page: 1,
          page_size: 250,
          data: [],
          total: 0,
          total_pages: 0,
        });
      }
    } catch (error) {
      console.error("Error deleting internal asset:", error);
    } finally {
      setDeletingInternalAssetKey(null);
    }
  }, [
    environmentId,
    fetchDataSources,
    multipleSelection,
    selectedEndpoints,
    setActiveRelatedEntity,
    setBronzeSelectionIdentity,
    setData,
    setMultipleSelection,
    setSelectedDataMarts,
    setSelectedDataMartEntities,
    setSelectedEndpoints,
  ]);

  const handleRelatedNodeToggle = (entity: IDataVaultRelatedEntity) => {
    if (
      activeRelatedEntity &&
      activeRelatedEntity.target_entity_rdv_id === entity.target_entity_rdv_id
    ) {
      setActiveRelatedEntity(null);
    } else {
      setActiveRelatedEntity(entity);
      setMultipleSelection([]);
      setSelectedEndpoints([]);
      setSelectedDataMarts([]);
      setSelectedDataMartEntities([]);
    }
  };

  const hasGoldSelection =
    Array.isArray(selectedDataMartEntities) && selectedDataMartEntities.length > 0;
  const hasBronzeObjectSelection =
    Array.isArray(multipleSelection) &&
    multipleSelection.length > 0 &&
    Array.isArray(selectedEndpoints) &&
    selectedEndpoints.length > 0;

  const fetchResults = useCallback(async () => {
    if (hasGoldSelection) {
      // Gold selections are handled by dedicated APIs in the catalogue hook.
      return;
    }
    if (!hasBronzeObjectSelection) {
      setData({
        items: [],
        total_count: 0,
        page: 1,
        page_size: 250,
        data: [],
        total: 0,
        total_pages: 0,
      });
      return;
    }
    try {
      const selectedDataSource = findDataSourceByName(multipleSelection?.[0]);
      const selectedEndpoint = selectedDataSource?.endpoints.find(
        endpoint => endpoint.source_entity_name === selectedEndpoints?.[0]
      );
      const res = await getBronzeCatalogColumns({
        sourceEntityId: Number(selectedEndpoint?.source_entity_id ?? null),
        page: 1,
        limit: 250,
      });
      // if (res.data.length === 0) {
      //   setShowErrorModal(true);
      //   // roll back only the last selection
      //   // if (lastChange.current) {
      //   //    const { type, value } = lastChange.current;
      //   //    if (type === 'ds') {
      //   //      setMultipleSelection((prev:string[]) => prev.filter(x => x !== value));
      //   //      setExpandedDataSources(prev => prev.filter(x => x !== value));
      //   //    } else {
      //   //      setSelectedEndpoints((prev :string[])=> prev.filter(x => x !== value));
      //   //    }
      //   //  }
      //   if (lastChange.current) {
      //     const change = lastChange.current;
      //     if (change.type === 'ds') {
      //       // undo data-source only
      //       setMultipleSelection((prev:string[]) => prev.filter(x => x !== change.value));
      //       setExpandedDataSources(prev => prev.filter(x => x !== change.value));
      //     } else {
      //       // undo endpoint *and* its parent data-source
      //       setSelectedEndpoints((prev:string[]) => prev.filter(x => x !== change.value));
      //       setMultipleSelection((prev:string[]) => prev.filter(x => x !== change.parent));
      //       setExpandedDataSources(prev => prev.filter(x => x !== change.parent));
      //     }
      //   }
      //    // clear ref
      //    lastChange.current = null;
      //   // setMultipleSelection([]);
      //   // setSelectedEndpoints([]);
      //   // setExpandedDataSources([]);
      // } else {
      //   setData(res);
      // }
      const payload = Array.isArray(res?.items) ? res.items : [];
      const hasResolvedEntity = Boolean(res?.entity?.source_entity_id || res?.entity?.source_entity_name);
      if (payload.length === 0 && !hasResolvedEntity) {
        setShowErrorModal(true);

        if (lastChange.current) {
          const change = lastChange.current;

          if (change.type === 'ds') {
            const dsName = change.value;
            const ds = findDataSourceByName(dsName);
            setMultipleSelection((ms:string[]) => ms.filter(x => x !== dsName));
            if (ds) {
              const endpointNames = ds.endpoints.map(endpoint => endpoint.source_entity_name);
              setSelectedEndpoints((se:string[]) => se.filter(e => !endpointNames.includes(e)));
            }
          } else if (change.type === 'ep') {
            const ep = change.value;
            const dsName = change.parent!;
            setSelectedEndpoints((se:string[]) => se.filter(x => x !== ep));

            const ds = findDataSourceByName(dsName);
            if (ds) {
              const endpointNames = ds.endpoints.map(endpoint => endpoint.source_entity_name);
              const remainingEps = selectedEndpoints
                .filter(x => x !== ep)
                .filter(x => endpointNames.includes(x));
              if (remainingEps.length === 0) {
                setMultipleSelection((ms:string[]) => ms.filter(x => x !== dsName));
              }
            }
          } else if (change.type === 'dm') {
            const martName = change.value;
            setSelectedDataMarts((prev: string[] = []) => prev.filter(name => name !== martName));
            setSelectedDataMartEntities((prev: string[] = []) =>
              prev.filter(entity => !change.entities.includes(entity))
            );
          } else if (change.type === 'dmEntity') {
            const martName = change.parent;
            const updatedEntities = selectedDataMartEntities.filter(
              entity => entity !== change.value
            );
            setSelectedDataMartEntities(updatedEntities);
            const group = martName ? findDataMartGroup(martName) : undefined;
            const stillSelected =
              group?.entities.some(entity => updatedEntities.includes(entity.name)) ?? false;
            if (!stillSelected && martName) {
              setSelectedDataMarts((prev: string[] = []) =>
                prev.filter(name => name !== martName)
              );
            }
          }
          lastChange.current = null;
        }

      } else {
        const normalizedItems = normalizeBronzeCatalogRows(payload, res?.entity);
        setData({
          ...res,
          items: normalizedItems,
          total_count: normalizedItems.length,
          data: normalizedItems,
          total: normalizedItems.length,
        });
        lastChange.current = null;
      }
    } catch (err) {
      console.error('Error fetching results:', err);
    }
  }, [
    hasBronzeObjectSelection,
    hasGoldSelection,
    multipleSelection,
    selectedDataMartEntities,
    selectedEndpoints,
    setData,
    setMultipleSelection,
    setSelectedDataMartEntities,
    setSelectedDataMarts,
    setSelectedEndpoints,
    setShowErrorModal,
    findDataMartGroup,
    findDataSourceByName,
  ]);

  // const handleSave = () => {
  //   fetchResults();
  //   // @ts-ignore
  //   setExpandedDataSources((prev:any) => [...new Set([...prev, ...multipleSelection])]);
  // };

  useEffect(() => {
    if (!hasMountedFilters.current) {
      hasMountedFilters.current = true;
      return;
    }
    if (!hasGoldSelection) {
      fetchResults();
    }
  }, [multipleSelection, selectedEndpoints, selectedDataMartEntities, hasGoldSelection, fetchResults]);

  const renderEmptyState = (title: string, subtitle: string, accent: string) => (
    <div className="modern-empty-state">
      <FaDatabase size={28} color={accent} />
      <p className="empty-title">{title}</p>
      <p className="empty-subtitle">{subtitle}</p>
    </div>
  );

  const renderCoreSection = () => {
    const filteredSources = dataSourceList
      .map(ds => ({
        ...ds,
        endpoints:
          normalizedSearchTerm.length === 0
            ? ds.endpoints
            : ds.endpoints.filter(endpoint =>
                endpoint.source_entity_name.toLowerCase().includes(normalizedSearchTerm)
              ),
      }))
      .filter(ds => {
        if (normalizedSearchTerm.length === 0) {
          return true;
        }
        return (
          ds.data_source_name.toLowerCase().includes(normalizedSearchTerm) ||
          ds.endpoints.length > 0
        );
      })
      // Alphabetical, sources and their tables alike. They arrived in creation order, which is
      // unfindable once a customer has a few hundred of them.
      .map(ds => ({
        ...ds,
        endpoints: [...ds.endpoints].sort((left, right) =>
          compareNames(left.source_entity_name, right.source_entity_name)
        ),
      }))
      .sort((left, right) => compareNames(left.data_source_name, right.data_source_name));

    const internalSources = filteredSources.filter(ds => {
      if (ds.data_source_type !== "INTERNAL_REGISTERED") {
        return false;
      }

      if (ds.data_source_name === INTERNAL_BRONZE_ROOT_NAME) {
        return true;
      }

      return ds.endpoints.some(endpoint => endpoint.source_entity_layer === "bronze");
    });
    const ingestedSources = filteredSources.filter(ds => ds.data_source_type !== "INTERNAL_REGISTERED");

    const renderSourceCollection = (sources: DataSourceListItem[], title: string, subtitle: string, internal: boolean) => {
      if (sources.length === 0) {
        return (
          <div className="catalogue-tree__group-empty">
            {internal
              ? "No custom source entities registered yet."
              : "No ingested sources match the current search."}
          </div>
        );
      }

      return (
        <div className="catalogue-tree__group">
          <div className="catalogue-tree__group-header">
            <div>
              <p className="catalogue-tree__group-title">{title}</p>
              <p className="catalogue-tree__group-subtitle">{subtitle}</p>
            </div>
            {internal && (
              <span className="catalogue-tree__group-badge">Internal</span>
            )}
          </div>
          <div className="catalogue-tree">
            {sources.map(ds => {
              const dsKey = createDataSourceKey("primary", ds.data_source_name);
              const expanded = expandedDataSources?.includes(dsKey);
              const selected = multipleSelection?.includes(ds.data_source_name);
              return (
                <div key={`primary-${ds.data_source_name}`} className="catalogue-tree__branch">
                  <div className={`catalogue-tree__row ${selected ? 'is-selected' : ''}`}>
                    <button
                      className="catalogue-tree__toggle"
                      onClick={() => toggleDataSource("primary", ds.data_source_name)}
                    >
                      {expanded ? <FaChevronDown /> : <FaChevronRight />}
                    </button>
                    <button
                      className="catalogue-tree__label"
                      onClick={() => handleDataSourceSelect(ds.data_source_name, "primary")}
                      title={ds.data_source_name}
                    >
                      {internal ? (
                        <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                      ) : (
                        <FaDatabase className="catalogue-tree__icon bronze" />
                      )}
                      <span className="catalogue-tree__text">{ds.data_source_name}</span>
                    </button>
                  </div>
                  {expanded && ds.endpoints.length > 0 && (
                    <div className="catalogue-tree__children">
                      {ds.endpoints.map(endpoint => {
                        const endpointSelected = selectedEndpoints?.includes(endpoint.source_entity_name);
                        return (
                          <div key={`${ds.data_source_name}-${endpoint.source_entity_name}`} className={`catalogue-tree__row is-leaf ${endpointSelected ? 'is-selected' : ''}`}>
                            <span className="catalogue-tree__spacer" />
                            <button
                              className="catalogue-tree__label"
                              onClick={() => handleEndpointSelect(endpoint.source_entity_name, ds.data_source_name, "primary")}
                              title={endpoint.source_entity_name}
                            >
                              {internal ? (
                                <FaTable className="catalogue-tree__icon bronze" />
                              ) : (
                                <FaTable className="catalogue-tree__icon bronze" />
                              )}
                              <span className="catalogue-tree__text">{endpoint.source_entity_name}</span>
                              {internal && (
                                <span className="catalogue-tree__row-badge is-soft">
                                  {[endpoint.source_entity_layer, endpoint.source_entity_kind].filter(Boolean).join(" · ") || "registered"}
                                </span>
                              )}
                            </button>
                            {internal && (
                              <button
                                className="catalogue-tree__icon-action"
                                onClick={() => handleOpenEditModal(ds, endpoint)}
                                title={`Edit ${endpoint.source_entity_name}`}
                                aria-label={`Edit ${endpoint.source_entity_name}`}
                              >
                                <FaPen />
                              </button>
                            )}
                            {internal && (
                              <button
                                className="catalogue-tree__icon-action catalogue-tree__icon-action--danger"
                                onClick={() => handleDeleteInternalAsset(ds, endpoint, "bronze")}
                                title={`Delete ${endpoint.source_entity_name}`}
                                aria-label={`Delete ${endpoint.source_entity_name}`}
                                disabled={deletingInternalAssetKey === `${ds.data_source_id}:${endpoint.source_entity_id}`}
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    if (filteredSources.length === 0) {
      return (
        <div className="catalogue-tree__group">
          <div className="catalogue-tree__group-header">
            <div>
              <p className="catalogue-tree__group-title">Bronze sources</p>
              <p className="catalogue-tree__group-subtitle">No sources match the current search.</p>
            </div>
          </div>
          {renderEmptyState("No datasets available", "Update filters or try another environment.", "#a8b3c5")}
        </div>
      );
    }

    return (
      <div className="catalogue-tree">
        <div className="catalogue-tree__group-header">
          <div>
            <p className="catalogue-tree__group-title">Bronze sources</p>
            <p className="catalogue-tree__group-subtitle">Browse ingested and internal registered source entities.</p>
          </div>
        </div>
        {renderSourceCollection(ingestedSources, "Ingested sources", "Connector-managed sources and ingested source entities.", false)}
        {renderSourceCollection(internalSources, "Custom source entities", "Customer-created entities registered directly in the portal.", true)}
      </div>
    );
  };

  const renderRelatedSection = () => {
    const filteredEntities = relatedEntities.filter(entity => {
      if (normalizedSearchTerm.length === 0) {
        return true;
      }
      return [
        entity?.target_entity_rdv_name,
        entity?.source_entity_name,
        entity?.data_source_name,
        entity?.target_schema,
      ]
        .filter((value): value is string => typeof value === "string")
        .some(value => value.toLowerCase().includes(normalizedSearchTerm));
    });

    if (filteredEntities.length === 0) {
      return renderEmptyState("No entities mapped", "Update filters or try another environment.", "#a8b3c5");
    }

    const internalSilverSources = dataSourceList
      .filter(ds =>
        ds.data_source_type === "INTERNAL_REGISTERED" &&
        (ds.data_source_name === INTERNAL_SILVER_ROOT_NAME ||
          ds.endpoints.some(endpoint => endpoint.source_entity_layer === "silver"))
      )
      .map(ds => ({
        ...ds,
        // source_entity_layer is not populated yet, so filtering on it hides everything.
        // Inside the per-layer container every endpoint is silver by construction; only
        // other data sources need the layer to opt an endpoint in.
        endpoints:
          ds.data_source_name === INTERNAL_SILVER_ROOT_NAME
            ? ds.endpoints
            : ds.endpoints.filter(endpoint => endpoint.source_entity_layer === "silver"),
      }))
      .filter(ds => {
        if (normalizedSearchTerm.length === 0) {
          return ds.endpoints.length > 0 || ds.data_source_name === INTERNAL_SILVER_ROOT_NAME;
        }
        return (
          ds.data_source_name.toLowerCase().includes(normalizedSearchTerm) ||
          ds.endpoints.some(endpoint =>
            endpoint.source_entity_name.toLowerCase().includes(normalizedSearchTerm)
          )
        );
      });

    // /fetch-related-entities returns one row per source-entity × RDV-mapping, so a single
    // entity that participates in several ensemble mappings (common for BDV source views like
    // bdv.geography_common_dahl_v) arrives as N identical-looking rows. The tree shows one node
    // per entity, so collapse the fan-out on the same identity the leaf renders below
    // (schema + target_entity_rdv_name || source_entity_name). Distinct entities that merely
    // share a source view keep different target_entity_rdv_name, so they are NOT merged.
    const relatedEntityNodeKey = (entity: IDataVaultRelatedEntity) => {
      const schema = (entity.target_schema || entity.source_schema || 'rdv').toLowerCase();
      const label = entity.target_entity_rdv_name || entity.source_entity_name || 'unassigned_entity';
      return `${schema}.${label}`;
    };
    // A customer-maintained object arrives twice: once as its registration in the custom
    // container, once as the MANUAL target entity the migration created for it. Both
    // collapse to the same node key below, so order decided which folder it landed in —
    // that is why some objects showed under Business Data Vault and others under Custom.
    // Let the registration win, so every customer-built object sits in one place.
    const registrationFirst = [...filteredEntities].sort((left, right) => {
      const leftIsRegistration = left.data_source_name === INTERNAL_SILVER_ROOT_NAME ? 0 : 1;
      const rightIsRegistration = right.data_source_name === INTERNAL_SILVER_ROOT_NAME ? 0 : 1;
      return leftIsRegistration - rightIsRegistration;
    });
    const seenRelatedEntities = new Set<string>();
    const dedupedEntities = registrationFirst.filter(entity => {
      const key = relatedEntityNodeKey(entity);
      if (seenRelatedEntities.has(key)) {
        return false;
      }
      seenRelatedEntities.add(key);
      return true;
    });

    const groupedEntities = dedupedEntities.reduce(
      (
        acc: {
          rawDataVault: IDataVaultRelatedEntity[];
          businessDataVault: IDataVaultRelatedEntity[];
          internalSilverAssets: IDataVaultRelatedEntity[];
        },
        entity
      ) => {
        // Only the per-layer custom container belongs here. Testing data_source_type
        // alone is far too wide: "Custom Source Entities" is INTERNAL_REGISTERED too and
        // feeds ~117 generated hubs/sats/links, which belong under Raw Data Vault.
        const isInternalAsset = entity?.data_source_name === INTERNAL_SILVER_ROOT_NAME;

        if (isInternalAsset) {
          acc.internalSilverAssets.push(entity);
          return acc;
        }

        const schema = (entity.target_schema ?? 'rdv').toLowerCase();
        if (schema === 'rdv') {
          acc.rawDataVault.push(entity);
        } else {
          acc.businessDataVault.push(entity);
        }
        return acc;
      },
      { rawDataVault: [], businessDataVault: [], internalSilverAssets: [] }
    );

    // Custom silver assets arrive from two places: entities the customer registered in the
    // per-layer data source (with edit/delete actions), and RDV-related entities whose data
    // source is INTERNAL_REGISTERED. Both are shown — the folder used to render only the
    // first, so with an unpopulated source_entity_layer it looked empty until a search term
    // removed the empty container and let the second list through.
    const registeredSilverEndpoints = internalSilverSources.flatMap(ds =>
      ds.endpoints.map(endpoint => ({ ds, endpoint }))
    );
    const registeredSilverNames = new Set(
      registeredSilverEndpoints
        .map(({ endpoint }) => endpoint.source_entity_name?.toLowerCase())
        .filter((name): name is string => Boolean(name))
    );

    const silverChildGroups = [
      {
        key: 'silver-raw-data-vault',
        title: 'Raw Data Vault',
        subtitle: 'Entities with target_schema = rdv',
        entities: groupedEntities.rawDataVault,
      },
      {
        key: 'silver-business-data-vault',
        title: 'Business Data Vault',
        subtitle: 'Entities with target_schema ≠ rdv',
        entities: groupedEntities.businessDataVault,
      },
      {
        key: 'silver-internal-assets',
        title: 'Custom Data Vault Entities',
        subtitle: 'Customer-created silver tables and views',
        entities: groupedEntities.internalSilverAssets,
      },
    ];

    return (
      <div className="catalogue-tree">
        {silverChildGroups.map(group => {
          const isGroupExpanded = expandedSilverGroups.includes(group.key);
          return (
            <div key={group.key} className="catalogue-tree__branch">
              <div className="catalogue-tree__row is-section">
                <button className="catalogue-tree__toggle" onClick={() => toggleSilverGroup(group.key)}>
                  {isGroupExpanded ? <FaChevronDown /> : <FaChevronRight />}
                </button>
                <button className="catalogue-tree__label" onClick={() => toggleSilverGroup(group.key)} title={group.title}>
                  {group.key === 'silver-internal-assets' ? (
                    <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                  ) : (
                    <FaDatabase className="catalogue-tree__icon silver" />
                  )}
                  <span className="catalogue-tree__text">{group.title}</span>
                </button>
              </div>
              {isGroupExpanded && (
                <div className="catalogue-tree__children">
                  {group.key === 'silver-internal-assets' ? (
                    registeredSilverEndpoints.length === 0 &&
                    group.entities.length === 0 ? (
                      renderEmptyState(
                        `No ${group.title} entities`,
                        'Update filters or try another environment.',
                        '#c0c0c0'
                      )
                    ) : (
                      <>
                      {registeredSilverEndpoints.map(({ ds, endpoint }) => {
                        const endpointName = endpoint.source_entity_name;
                        const endpointSelected = selectedEndpoints?.includes(endpointName);
                        return (
                          <div key={`silver-internal-${ds.data_source_name}-${endpointName}`} className={`catalogue-tree__row is-leaf ${endpointSelected ? 'is-selected' : ''}`}>
                            <span className="catalogue-tree__spacer" />
                            <button
                              className="catalogue-tree__label"
                              onClick={() => handleEndpointSelect(endpointName, ds.data_source_name, "related", "silver")}
                              title={endpointName}
                            >
                              <FaTable className="catalogue-tree__icon silver" />
                              <span className="catalogue-tree__text">{endpointName}</span>
                            </button>
                            <button
                              className="catalogue-tree__icon-action"
                              onClick={() => handleOpenEditModal(ds, endpoint)}
                              title={`Edit ${endpointName}`}
                              aria-label={`Edit ${endpointName}`}
                            >
                              <FaPen />
                            </button>
                            <button
                              className="catalogue-tree__icon-action catalogue-tree__icon-action--danger"
                              onClick={() => handleDeleteInternalAsset(ds, endpoint, "silver")}
                              title={`Delete ${endpointName}`}
                              aria-label={`Delete ${endpointName}`}
                              disabled={deletingInternalAssetKey === `${ds.data_source_id}:${endpoint.source_entity_id}`}
                            >
                              <FaTrash />
                            </button>
                          </div>
                        );
                      })}
                      {group.entities
                        .filter(entity => {
                          const name = (
                            entity.source_entity_name ||
                            entity.target_entity_rdv_name ||
                            ''
                          ).toLowerCase();
                          return !name || !registeredSilverNames.has(name);
                        })
                        .map((entity, idx) => {
                          const nodeKey = relatedEntityNodeKey(entity);
                          const isActive =
                            activeRelatedEntity &&
                            activeRelatedEntity.target_entity_rdv_id === entity.target_entity_rdv_id;
                          return (
                            <div
                              key={`${group.key}-${nodeKey}-${idx}`}
                              className={`catalogue-tree__row is-leaf ${isActive ? 'is-selected' : ''}`}
                            >
                              <span className="catalogue-tree__spacer" />
                              <button
                                className="catalogue-tree__label"
                                onClick={() => handleRelatedNodeToggle(entity)}
                                title={nodeKey}
                              >
                                <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                                <span className="catalogue-tree__text">{nodeKey}</span>
                              </button>
                            </div>
                          );
                        })}
                      </>
                    )
                  ) : group.entities.length === 0 ? (
                    renderEmptyState(
                      `No ${group.title} entities`,
                      'Update filters or try another environment.',
                      '#c0c0c0'
                    )
                  ) : (
                    group.entities.map((entity, idx) => {
                      const nodeKey = relatedEntityNodeKey(entity);
                      const isActive =
                        activeRelatedEntity &&
                        activeRelatedEntity.target_entity_rdv_id === entity.target_entity_rdv_id;

                      return (
                        <div
                          key={`${group.key}-${nodeKey}-${idx}`}
                          className={`catalogue-tree__row is-leaf ${isActive ? 'is-selected' : ''}`}
                        >
                          <span className="catalogue-tree__spacer" />
                          <button className="catalogue-tree__label" onClick={() => handleRelatedNodeToggle(entity)} title={nodeKey}>
                            {group.key === 'silver-internal-assets' ? (
                              <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                            ) : (
                              <FaTable className="catalogue-tree__icon silver" />
                            )}
                            <span className="catalogue-tree__text">{nodeKey}</span>
                          </button>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderDataMartSection = () => {
    if (isLoadingDataMartMappings) {
      return renderEmptyState("Loading data mart mappings", "Hold on while we fetch gold-layer entities.", "#d4af37");
    }
    const internalGoldSources = dataSourceList
      .filter((ds: DataSourceListItem) =>
        ds.data_source_type === "INTERNAL_REGISTERED" &&
        (ds.data_source_name === INTERNAL_GOLD_ROOT_NAME ||
          ds.endpoints.some(endpoint => endpoint.source_entity_layer === "gold"))
      )
      .map((ds: DataSourceListItem) => ({
        ...ds,
        endpoints: ds.endpoints.filter(endpoint => endpoint.source_entity_layer === "gold"),
      }))
      .filter((ds: DataSourceListItem) => {
        if (normalizedSearchTerm.length === 0) {
          return ds.endpoints.length > 0 || ds.data_source_name === INTERNAL_GOLD_ROOT_NAME;
        }
        return (
          ds.data_source_name.toLowerCase().includes(normalizedSearchTerm) ||
          ds.endpoints.some(endpoint =>
            endpoint.source_entity_name.toLowerCase().includes(normalizedSearchTerm)
          )
        );
      });
    const filteredDataMartGroups = dataMartGroups
      .map(group => ({
        ...group,
        entities:
          normalizedSearchTerm.length === 0
            ? group.entities
            : group.entities.filter(entity =>
                [group.datamart, entity.name, entity.table_construct, entity.target_entity_rdv_name]
                  .filter((value): value is string => typeof value === "string")
                  .some(value => value.toLowerCase().includes(normalizedSearchTerm))
              ),
      }))
      .filter(group => {
        if (normalizedSearchTerm.length === 0) {
          return true;
        }
        return (
          group.datamart.toLowerCase().includes(normalizedSearchTerm) ||
          group.entities.length > 0
        );
      });
    if (filteredDataMartGroups.length === 0) {
      return renderEmptyState("No data marts available", "Import a mapping or adjust your environment.", "#d4af37");
    }

    return (
      <div className="catalogue-tree">
        {filteredDataMartGroups.map(group => {
          const expanded = expandedDataMarts?.includes(group.datamart);
          const selected = selectedDataMarts?.includes(group.datamart);
          const isInternalGroup = group.datamart === INTERNAL_GOLD_ROOT_NAME;
          return (
            <div key={group.datamart} className="catalogue-tree__branch">
              <div className={`catalogue-tree__row ${selected ? 'is-selected' : ''}`}>
                <button className="catalogue-tree__toggle" onClick={() => toggleDataMartGroup(group.datamart)}>
                  {expanded ? <FaChevronDown /> : <FaChevronRight />}
                </button>
                <button className="catalogue-tree__label" onClick={() => handleDataMartGroupSelect(group.datamart)} title={group.datamart}>
                  {isInternalGroup ? (
                    <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                  ) : (
                    <FaDatabase className="catalogue-tree__icon gold" />
                  )}
                  <span className="catalogue-tree__text">{isInternalGroup ? INTERNAL_GOLD_ROOT_NAME : group.datamart}</span>
                </button>
              </div>
              {expanded && (
                <div className="catalogue-tree__children">
                  {group.entities.map((entity, entityIdx) => {
                    const entitySelected = selectedDataMartEntities?.includes(entity.name);
                    return (
                      <div key={`${group.datamart}-${entity.name}-${entityIdx}`} className={`catalogue-tree__row is-leaf ${entitySelected ? 'is-selected' : ''}`}>
                        <span className="catalogue-tree__spacer" />
                        <button className="catalogue-tree__label" onClick={() => handleDataMartEntitySelect(entity, group.datamart)} title={entity.name}>
                          {isInternalGroup ? (
                            <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                          ) : (
                            <FaTable className="catalogue-tree__icon gold" />
                          )}
                          <span className="catalogue-tree__text">{entity.name}</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
        {internalGoldSources.length > 0 && (
          <div className="catalogue-tree__branch">
            <div className="catalogue-tree__row">
              <button className="catalogue-tree__toggle" onClick={() => toggleDataMartGroup(INTERNAL_GOLD_ROOT_NAME)}>
                {expandedDataMarts?.includes(INTERNAL_GOLD_ROOT_NAME) ? <FaChevronDown /> : <FaChevronRight />}
              </button>
              <button className="catalogue-tree__label" onClick={() => toggleDataMartGroup(INTERNAL_GOLD_ROOT_NAME)} title={INTERNAL_GOLD_ROOT_NAME}>
                <FaRegFolderOpen className="catalogue-tree__icon bronze-internal" />
                <span className="catalogue-tree__text">{INTERNAL_GOLD_ROOT_NAME}</span>
              </button>
            </div>
            {expandedDataMarts?.includes(INTERNAL_GOLD_ROOT_NAME) && (
              <div className="catalogue-tree__children">
                {internalGoldSources.flatMap(ds =>
                  ds.endpoints.map(endpoint => {
                    const endpointName = endpoint.source_entity_name;
                    const endpointSelected = selectedEndpoints?.includes(endpointName);
                    return (
                      <div key={`gold-internal-${ds.data_source_name}-${endpointName}`} className={`catalogue-tree__row is-leaf ${endpointSelected ? 'is-selected' : ''}`}>
                        <span className="catalogue-tree__spacer" />
                        <button
                          className="catalogue-tree__label"
                          onClick={() => handleEndpointSelect(endpointName, ds.data_source_name, "dataMart", "gold")}
                          title={endpointName}
                        >
                          <FaTable className="catalogue-tree__icon gold" />
                          <span className="catalogue-tree__text">{endpointName}</span>
                        </button>
                        <button
                          className="catalogue-tree__icon-action"
                          onClick={() => handleOpenEditModal(ds, endpoint)}
                          title={`Edit ${endpointName}`}
                          aria-label={`Edit ${endpointName}`}
                        >
                          <FaPen />
                        </button>
                        <button
                          className="catalogue-tree__icon-action catalogue-tree__icon-action--danger"
                          onClick={() => handleDeleteInternalAsset(ds, endpoint, "gold")}
                          title={`Delete ${endpointName}`}
                          aria-label={`Delete ${endpointName}`}
                          disabled={deletingInternalAssetKey === `${ds.data_source_id}:${endpoint.source_entity_id}`}
                        >
                          <FaTrash />
                        </button>
                      </div>
                    );
                  })
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const categorySections: Array<{
    key: CategoryKey;
    title: string;
    subtitle: string;
    icon: ReactNode;
    accent: "primary" | "related" | "dataMart";
    content: ReactNode;
    createLayer: "bronze" | "silver" | "gold";
  }> = [
    {
      key: "category-primary",
      title: "Bronze",
      subtitle: "Data sources and source entities",
      icon: <FaDatabase size={18} color="#cd7f32" />,
      accent: "primary",
      content: renderCoreSection(),
      createLayer: "bronze",
    },
    {
      key: "category-related",
      title: "Silver",
      subtitle: "RDV, BDV, and custom data vault entities",
      icon: <FaDatabase size={18} color="#c0c0c0" />,
      accent: "related",
      content: renderRelatedSection(),
      createLayer: "silver",
    },
    {
      key: "category-dataMart",
      title: "Gold",
      subtitle: "Data marts and modeled entities",
      icon: <FaDatabase size={18} color="#d4af37" />,
      accent: "dataMart",
      content: renderDataMartSection(),
      createLayer: "gold",
    },
  ];

  return (
    <>
      <div className="selected-data-set-container tw-shadow-none tw-bg-transparent tw-p-0">
        <div className="accordion-container tw-gap-3 tw-p-0">
          <div className="catalogue-explorer-search">
            <FaSearch className="catalogue-explorer-search__icon" />
            <input
              value={searchTerm}
              onChange={event => setSearchTerm(event.target.value)}
              placeholder="Search layers, entities, or tables"
              className="catalogue-explorer-search__input"
            />
          </div>
          <div className="catalogue-layer-tree">
            {categorySections.map(section => {
              const isExpanded = expandedCategories.includes(section.key);
              return (
                <div key={section.key} className="catalogue-layer-tree__section">
                  <div className={`catalogue-layer-tree__header ${isExpanded ? 'is-open' : ''}`}>
                    <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center">
                      <button
                        className="catalogue-layer-tree__toggle"
                        onClick={() => toggleCategory(section.key)}
                      >
                        {isExpanded ? <FaChevronDown /> : <FaChevronRight />}
                      </button>
                      <button
                        className="catalogue-layer-tree__title"
                        onClick={() => toggleCategory(section.key)}
                        title={`${section.title} - ${section.subtitle}`}
                      >
                        <span className="catalogue-layer-tree__icon">{section.icon}</span>
                        <span className="catalogue-layer-tree__copy">
                          <span className="catalogue-layer-tree__name">{section.title}</span>
                          <span className="catalogue-layer-tree__subtitle">{section.subtitle}</span>
                        </span>
                      </button>
                    </div>
                    <button
                      className="catalogue-tree__icon-action"
                      onClick={() => handleOpenRegisterModal(section.createLayer)}
                      title="Register custom source entity"
                      aria-label="Register custom source entity"
                    >
                      <FaPlus />
                    </button>
                  </div>
                  {isExpanded && (
                    <div className="catalogue-layer-tree__body">
                      {section.content}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <Modal show={showRegisterModal} onHide={() => setShowRegisterModal(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editingInternalAsset ? "Edit custom source entity" : "Register custom source entity"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="catalogue-register-modal__intro">
            {editingInternalAsset
              ? "Update this custom source entity. Layer is fixed after creation (it determines the internal root the entity lives under). Columns are managed separately."
              : "Register a custom Bronze, Silver, or Gold source entity under the standard system-managed internal root for that layer."}
          </div>

          <div className="catalogue-register-modal__section">
            <div className="catalogue-register-modal__section-header">
              <h5>Asset definition</h5>
            </div>
            <div className="catalogue-register-modal__grid">
              <Form.Group>
                <Form.Label>Source entity name</Form.Label>
                <Form.Control
                  value={internalAssetForm.sourceEntityName}
                  onChange={event => updateInternalAssetForm("sourceEntityName", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Source schema</Form.Label>
                <Form.Control
                  value={internalAssetForm.sourceSchema}
                  onChange={event => updateInternalAssetForm("sourceSchema", event.target.value)}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label>Layer</Form.Label>
                <Form.Select
                  value={internalAssetForm.sourceEntityLayer}
                  disabled={!!editingInternalAsset}
                  onChange={event => updateInternalAssetForm("sourceEntityLayer", event.target.value)}
                >
                  {INTERNAL_ASSET_LAYER_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Kind</Form.Label>
                <Form.Select
                  value={internalAssetForm.sourceEntityKind}
                  onChange={event => updateInternalAssetForm("sourceEntityKind", event.target.value)}
                >
                  {INTERNAL_ASSET_KIND_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
              <Form.Group>
                <Form.Label>Description</Form.Label>
                <Form.Control
                  value={internalAssetForm.sourceEntityDescription}
                  onChange={event => updateInternalAssetForm("sourceEntityDescription", event.target.value)}
                />
              </Form.Group>
              <Form.Group className="tw-col-span-2">
                <Form.Label>Definition SQL</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  value={internalAssetForm.definitionSql}
                  onChange={event => updateInternalAssetForm("definitionSql", event.target.value)}
                />
              </Form.Group>
            </div>
          </div>

          {!editingInternalAsset && (
            <div className="catalogue-register-modal__intro tw-text-sm tw-text-slate-500">
              Columns are added after the entity is created — select it in the catalog and use
              “Add column” to add them in bulk.
            </div>
          )}
          {registerInternalAssetError ? (
            <div className="catalogue-register-modal__error">
              {registerInternalAssetError}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowRegisterModal(false)}>
            Cancel
          </Button>
          <Button
            style={{ background: "#0a2f63", borderColor: "#0a2f63" }}
            onClick={handleRegisterInternalAsset}
            disabled={registeringInternalAsset}
          >
            {registeringInternalAsset
              ? (editingInternalAsset ? "Saving..." : "Registering...")
              : (editingInternalAsset ? "Save changes" : "Register asset")}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SelectedDataSet;
